import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Combine, Hexagon, Image as ImageIcon, ImageOff, Info, MessagesSquare, MoreVertical, RefreshCw, Scan, Shapes, Sigma, Trash2, Type } from 'lucide-react';

import { GrSteps } from '../../components/icons/GrSteps';
import { DocumentMathTextBlock } from '../../components/MathTextBlock';
import { AiGeneratedInlineMark, AI_GENERATED_MARK_ON_TTQ_CARD_ENABLED } from '../../components/AiGeneratedContentBadge';
import { resolveKnowledgeParticleQaDisplay } from '../../components/KnowledgeParticleQaSections';
import type { TtqSessionParticleEntry } from '../../services/ttqService';
import { knowledgeService } from '../../services/knowledgeService';
import { buildKnowledgeParticleDetailPath } from '../../utils/knowledgeNavigation';
import { resolveTtqParticleTitle } from '../../utils/ttqDisplay';
import {
  formatTtqModelAuditDisplay,
  resolveParticleModelAudit,
} from '../../utils/ttqModelBrand';
import { TtqibEmbeddedImage } from './TtqibEmbeddedImage';
import { useParticleEmbeddedImage } from './useParticleEmbeddedImage';
import { useTtqWorkbenchParticle } from './useTtqWorkbenchParticle';
import { markTtqReturnFromKnowledge } from './ttqSessionUiState';
import { assessMultimodalBias } from './ttqParticleMultimodalBias';
import { ttqCanvasGesture } from './ttqCanvasGesture';
import { buildQuestionExplosion } from './ttqQuestionExplosionEngine';
import {
  isSegmentTasted,
  mergeProvenanceFromParticleMetadata,
  resolveDerivedOriginPresentation,
  type TtqDerivedProvenanceView,
} from './ttqibDerivedStack';
import { TTQIB_CARD_WIDTH } from './ttqibObjectLayout';
import { TtqPinTiIcon } from './TtqPinTiIcon';
import { TtqUpdateAbbrExprDialog } from './TtqUpdateAbbrExprDialog';
import {
  findChatAnchorForSegment,
  type AiChatSegmentAnchor,
} from '../../utils/aiChatSegmentKey';
import {
  answerSegmentObjectId,
  imageObjectId,
  segmentObjectId,
  wholeObjectId,
} from './types';
import type { TtqibCanvasObjectView } from './types';

type ExplosionSegmentView = {
  objectId: string;
  segmentIndex: number;
  segmentId?: string;
  text: string;
};

export type TtqParticleCardView = {
  particleId: string;
  layerId: string;
  title: string;
  x: number;
  y: number;
  width: number;
  whole: TtqibCanvasObjectView;
  mainSegments: ExplosionSegmentView[];
  subQuestions: Array<{
    order: number;
    segments: ExplosionSegmentView[];
  }>;
  imageObjectId: string;
};

type TtqParticleCardProps = {
  card: TtqParticleCardView;
  selectedObjectId: string | null;
  selectedSegmentObjectIds?: string[];
  tastedAnchorTexts?: string[];
  isDerived?: boolean;
  provenance?: TtqDerivedProvenanceView | null;
  /** 画布显示编号：`1` / `1.2` */
  displayNumber?: string;
  dragOffset?: { x: number; y: number } | null;
  /** interactive = 一对多画布；preview = 鸟瞰浮窗（无爆炸选段 / 无品题） */
  mode?: 'interactive' | 'preview';
  onSelectWhole?: (objectId: string) => void;
  onToggleSegment?: (item: { objectId: string; text: string; particleId: string }) => void;
  onSelectImage?: (objectId: string) => void;
  onOpenPinTi?: (particleId: string) => void;
  /** 打开该颗粒的数学阶梯练习（DRS） */
  onOpenLadderPractice?: (particleId: string) => void;
  showLadderPractice?: boolean;
  /** 本颗粒上已有对话锚点 */
  chatAnchors?: AiChatSegmentAnchor[];
  /** 点击段旁对话图标 */
  onOpenChatThread?: (threadId: string) => void;
  /** @deprecated 一对多/多对一节点不再可拖；保留以免旧调用报错 */
  onDragStart?: (particleId: string, clientX: number, clientY: number, pointerId: number) => void;
  /** 将本卡适配居中到画布视口中央 */
  onCenterInView?: (particleId: string) => void;
  /**
   * 在卡片空白处按下后平移整画布（节点本身不可拖）。
   * Mac 触控板「三指拖移」在浏览器里通常只表现为单指针，必须靠这条路径才能在卡片上拖动画布。
   */
  onCanvasPanStart?: (particleId: string, clientX: number, clientY: number, pointerId: number) => void;
  onDeleteDerived?: (particleId: string) => void;
  onRegenerateDerived?: (particleId: string) => void;
  /** CTTQ：进入「从现有节点中挑选成员」模式 */
  onAddCttqMember?: (particleId: string) => void;
  regeneratingDerivedId?: string | null;
  /** 任一 CTTQ 正在聚合时，所有收敛卡菜单锁定为「聚合中」 */
  cttqAggregating?: boolean;
  onMeasureHeight?: (particleId: string, size: { width: number; height: number }) => void;
  linkedRefs?: Array<{ particleId: string; title: string }>;
  onFocusLinked?: (particleId: string) => void;
};

function buildSegments(
  particleId: string,
  objects: TtqibCanvasObjectView[],
  text: string,
  indexOffset: number,
  idFactory: (particleId: string, segmentIndex: number) => string = segmentObjectId,
): ExplosionSegmentView[] {
  const explosion = buildQuestionExplosion(text);
  return explosion.segments.map((segment, index) => {
    const segmentIndex = indexOffset + index;
    const objectId = idFactory(particleId, segmentIndex);
    const stored = objects.find((item) => item.id === objectId);
    return {
      objectId,
      segmentIndex,
      segmentId: stored?.segmentId,
      text: segment.text,
    };
  });
}

export function buildParticleCards(objects: TtqibCanvasObjectView[]): TtqParticleCardView[] {
  return objects
    .filter((item) => item.type === 'particle-whole')
    .map((whole) => {
      const entry = whole.particleEntry;
      const display = resolveKnowledgeParticleQaDisplay({
        questionSource: entry?.particleQuestions,
        answerSource: null,
        fallbackQuestionText: whole.text || entry?.particleTitle || undefined,
      }).questionDisplay;

      let indexOffset = 0;
      const mainText = display.main.trim();
      const mainSegments = mainText
        ? buildSegments(whole.particleId, objects, mainText, indexOffset)
        : [];
      indexOffset += mainSegments.length;

      const subQuestions = display.items
        .map((item) => item.trim())
        .filter(Boolean)
        .map((item, order) => {
          const segments = buildSegments(whole.particleId, objects, item, indexOffset);
          indexOffset += segments.length;
          return { order: order + 1, segments };
        });

      return {
        particleId: whole.particleId,
        layerId: whole.layerId,
        title: whole.particleTitle || whole.particleId,
        x: whole.x,
        y: whole.y,
        width: Math.max(whole.width ?? TTQIB_CARD_WIDTH, TTQIB_CARD_WIDTH),
        whole,
        mainSegments,
        subQuestions,
        imageObjectId: imageObjectId(whole.particleId),
      };
    });
}

/** 从会话颗粒条目构造预览卡（鸟瞰浮窗等，不依赖 canvas objects） */
export function buildParticleCardFromEntry(
  entry: TtqSessionParticleEntry,
  options?: { x?: number; y?: number; width?: number; layerId?: string },
): TtqParticleCardView {
  const width = options?.width ?? 520;
  const layerId = options?.layerId ?? 'birds-eye-preview';
  const title = resolveTtqParticleTitle(entry, 0);
  const whole: TtqibCanvasObjectView = {
    id: wholeObjectId(entry.particleId),
    type: 'particle-whole',
    layerId,
    particleId: entry.particleId,
    x: options?.x ?? 0,
    y: options?.y ?? 0,
    width,
    text: title,
    particleEntry: entry,
    particleTitle: title,
  };
  const display = resolveKnowledgeParticleQaDisplay({
    questionSource: entry.particleQuestions,
    answerSource: null,
    fallbackQuestionText: title,
  }).questionDisplay;

  let indexOffset = 0;
  const mainText = display.main.trim();
  const mainSegments = mainText
    ? buildSegments(entry.particleId, [whole], mainText, indexOffset)
    : [];
  indexOffset += mainSegments.length;
  const subQuestions = display.items
    .map((item) => item.trim())
    .filter(Boolean)
    .map((item, order) => {
      const segments = buildSegments(entry.particleId, [whole], item, indexOffset);
      indexOffset += segments.length;
      return { order: order + 1, segments };
    });

  return {
    particleId: entry.particleId,
    layerId,
    title,
    x: whole.x,
    y: whole.y,
    width,
    whole,
    mainSegments,
    subQuestions,
    imageObjectId: imageObjectId(entry.particleId),
  };
}

function InlineExplosionText({
  segments,
  selectedSegmentObjectIds,
  tastedAnchorTexts = [],
  chatAnchors = [],
  particleId,
  onToggleSegment,
  onOpenChatThread,
  readOnly = false,
  showAiMark = false,
}: {
  segments: ExplosionSegmentView[];
  selectedSegmentObjectIds: string[];
  tastedAnchorTexts?: string[];
  chatAnchors?: AiChatSegmentAnchor[];
  particleId: string;
  onToggleSegment?: (item: { objectId: string; text: string; particleId: string }) => void;
  onOpenChatThread?: (threadId: string) => void;
  readOnly?: boolean;
  showAiMark?: boolean;
}) {
  const pendingSegmentRef = useRef<{
    pointerId: number;
    item: { objectId: string; text: string; particleId: string };
  } | null>(null);

  if (segments.length === 0) return null;
  if (readOnly || !onToggleSegment) {
    const plain = segments.map((segment) => segment.text).join('');
    return (
      <div className="ai-chat-assistant-body document-math-text text-[16px] leading-[1.65] text-ink">
        <DocumentMathTextBlock text={plain} />
        {showAiMark && AI_GENERATED_MARK_ON_TTQ_CARD_ENABLED ? (
          <AiGeneratedInlineMark baseFontSize={16} />
        ) : null}
      </div>
    );
  }
  const selectedSet = new Set(selectedSegmentObjectIds);
  return (
    <div className="document-math-text text-[16px] leading-[1.65] text-ink">
      {segments.map((segment) => {
        const selected = selectedSet.has(segment.objectId);
        const tasted = isSegmentTasted(segment.text, tastedAnchorTexts);
        const chatAnchor = findChatAnchorForSegment(chatAnchors, segment.text);
        return (
          <span
            key={segment.objectId}
            role="button"
            tabIndex={0}
            data-ttq-segment="true"
            data-object-id={segment.objectId}
            aria-pressed={selected}
            className={
              `ttq-explosion-segment inline px-0.5 align-baseline `
              + (selected ? 'is-selected ' : '')
              + (tasted ? 'is-tasted ' : '')
            }
            title={tasted ? '已品题线索' : undefined}
            onPointerDown={(event) => {
              event.stopPropagation();
              event.preventDefault();
              if (ttqCanvasGesture.isSuppressingUi()) return;
              pendingSegmentRef.current = {
                pointerId: event.pointerId,
                item: {
                  objectId: segment.objectId,
                  text: segment.text,
                  particleId,
                },
              };
            }}
            onPointerUp={(event) => {
              const pending = pendingSegmentRef.current;
              pendingSegmentRef.current = null;
              if (!pending || pending.pointerId !== event.pointerId) return;
              if (ttqCanvasGesture.isSuppressingUi() || ttqCanvasGesture.shouldBlockClick()) return;
              onToggleSegment(pending.item);
            }}
            onPointerCancel={() => {
              pendingSegmentRef.current = null;
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                if (ttqCanvasGesture.isSuppressingUi()) return;
                onToggleSegment({
                  objectId: segment.objectId,
                  text: segment.text,
                  particleId,
                });
              }
            }}
          >
            <DocumentMathTextBlock text={segment.text} inline />
            {chatAnchor?.threadId && onOpenChatThread ? (
              <button
                type="button"
                className="ttq-segment-chat-icon ml-0.5 inline-flex h-4 w-4 translate-y-[-1px] items-center justify-center rounded text-primary/80 hover:bg-primary/10 hover:text-primary"
                title="查看该段对话"
                aria-label="查看该段对话"
                onPointerDown={(event) => {
                  event.stopPropagation();
                  event.preventDefault();
                  pendingSegmentRef.current = null;
                }}
                onClick={(event) => {
                  event.stopPropagation();
                  event.preventDefault();
                  onOpenChatThread(chatAnchor.threadId);
                }}
              >
                <MessagesSquare size={12} strokeWidth={2.2} />
              </button>
            ) : null}
          </span>
        );
      })}
      {showAiMark && AI_GENERATED_MARK_ON_TTQ_CARD_ENABLED ? (
        <AiGeneratedInlineMark baseFontSize={16} />
      ) : null}
    </div>
  );
}

function PreviewQaSections({
  mainText,
  items,
  emptyLabel,
}: {
  mainText: string;
  items: string[];
  emptyLabel: string;
}) {
  if (!mainText.trim() && items.length === 0) {
    return <p className="text-sm text-muted">{emptyLabel}</p>;
  }
  return (
    <div className="space-y-3 text-[16px] leading-[1.65] text-ink">
      {mainText.trim() ? (
        <div className="ai-chat-assistant-body">
          <DocumentMathTextBlock text={mainText} />
          {AI_GENERATED_MARK_ON_TTQ_CARD_ENABLED ? (
            <AiGeneratedInlineMark baseFontSize={16} />
          ) : null}
        </div>
      ) : null}
      {items.map((item, order) => (
        <div key={`preview-sub-${order}`}>
          <div className="mb-2 border-t border-stone-300/70" />
          <div className="flex gap-3">
            <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-medium text-primary">
              {order + 1}
            </span>
            <div className="ai-chat-assistant-body min-w-0 flex-1">
              <DocumentMathTextBlock text={item} />
              {AI_GENERATED_MARK_ON_TTQ_CARD_ENABLED ? (
                <AiGeneratedInlineMark baseFontSize={16} />
              ) : null}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function QaBlockList({
  mainSegments,
  subs,
  selectedSegmentObjectIds,
  tastedAnchorTexts = [],
  chatAnchors = [],
  particleId,
  onToggleSegment,
  onOpenChatThread,
  onOpenPinTi,
  onOpenLadderPractice,
  showLadderPractice = false,
  emptyLabel,
  showPinTiToolbar,
  readOnly = false,
}: {
  mainSegments: ExplosionSegmentView[];
  subs: Array<{ order: number; segments: ExplosionSegmentView[] }>;
  selectedSegmentObjectIds: string[];
  tastedAnchorTexts?: string[];
  chatAnchors?: AiChatSegmentAnchor[];
  particleId: string;
  onToggleSegment?: (item: { objectId: string; text: string; particleId: string }) => void;
  onOpenChatThread?: (threadId: string) => void;
  onOpenPinTi?: (particleId: string) => void;
  onOpenLadderPractice?: (particleId: string) => void;
  showLadderPractice?: boolean;
  emptyLabel: string;
  showPinTiToolbar?: boolean;
  readOnly?: boolean;
}) {
  if (mainSegments.length === 0 && subs.length === 0) {
    return <p className="text-sm text-muted">{emptyLabel}</p>;
  }

  return (
    <div className="space-y-3">
      {mainSegments.length > 0 ? (
        <InlineExplosionText
          segments={mainSegments}
          selectedSegmentObjectIds={selectedSegmentObjectIds}
          tastedAnchorTexts={tastedAnchorTexts}
          chatAnchors={chatAnchors}
          particleId={particleId}
          onToggleSegment={onToggleSegment}
          onOpenChatThread={onOpenChatThread}
          readOnly={readOnly}
          showAiMark
        />
      ) : null}

      {showPinTiToolbar && (onOpenPinTi || (showLadderPractice && onOpenLadderPractice)) ? (
        <div
          className="flex h-7 items-center justify-end gap-1.5"
          data-ttq-toolbar="true"
        >
          {showLadderPractice && onOpenLadderPractice ? (
            <button
              type="button"
              className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-primary/35 bg-primary/10 text-primary-strong hover:bg-primary/15"
              aria-label="阶梯练习"
              title="阶梯练习"
              onPointerDown={(event) => {
                event.stopPropagation();
                event.preventDefault();
                if (ttqCanvasGesture.isSuppressingUi()) return;
                onOpenLadderPractice(particleId);
              }}
            >
              <GrSteps size={14} />
            </button>
          ) : null}
          {onOpenPinTi ? (
            <button
              type="button"
              className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-primary/35 bg-primary/10 text-primary hover:bg-primary/15"
              aria-label="品题"
              title="品题"
              onPointerDown={(event) => {
                event.stopPropagation();
                event.preventDefault();
                if (ttqCanvasGesture.isSuppressingUi()) return;
                onOpenPinTi(particleId);
              }}
            >
              <TtqPinTiIcon size={14} />
            </button>
          ) : null}
        </div>
      ) : null}

      {subs.map((sub) => (
        <div key={`sub-${sub.order}`}>
          <div className="mb-2 border-t border-stone-300/70" />
          <div className="flex gap-3">
            <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-medium text-primary">
              {sub.order}
            </span>
            <div className="min-w-0 flex-1">
              <InlineExplosionText
                segments={sub.segments}
                selectedSegmentObjectIds={selectedSegmentObjectIds}
                tastedAnchorTexts={tastedAnchorTexts}
                chatAnchors={chatAnchors}
                particleId={particleId}
                onToggleSegment={onToggleSegment}
                onOpenChatThread={onOpenChatThread}
                readOnly={readOnly}
                showAiMark
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export function TtqParticleCard({
  card,
  selectedObjectId,
  selectedSegmentObjectIds = [],
  tastedAnchorTexts = [],
  chatAnchors = [],
  isDerived = false,
  provenance = null,
  displayNumber,
  dragOffset,
  mode = 'interactive',
  onSelectWhole,
  onToggleSegment,
  onSelectImage,
  onOpenPinTi,
  onOpenLadderPractice,
  showLadderPractice = false,
  onOpenChatThread,
  onCenterInView,
  onCanvasPanStart,
  onDeleteDerived,
  onRegenerateDerived,
  onAddCttqMember,
  regeneratingDerivedId = null,
  cttqAggregating = false,
  onMeasureHeight,
  linkedRefs = [],
  onFocusLinked,
}: TtqParticleCardProps) {
  const preview = mode === 'preview';
  const location = useLocation();
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [materialOpen, setMaterialOpen] = useState(false);
  const [answersOpen, setAnswersOpen] = useState(false);
  const [provenanceOpen, setProvenanceOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [multimodalHintOpen, setMultimodalHintOpen] = useState(false);
  const [abbrExprDialogOpen, setAbbrExprDialogOpen] = useState(false);
  const [abbrExprSaving, setAbbrExprSaving] = useState(false);
  const [abbrExprError, setAbbrExprError] = useState<string | null>(null);
  const moreRef = useRef<HTMLDivElement | null>(null);
  const knowledgeDetailHref = useMemo(
    () => buildKnowledgeParticleDetailPath(
      card.particleId,
      `${location.pathname}${location.search}`,
    ),
    [card.particleId, location.pathname, location.search],
  );
  const ttqaIdFromPath = useMemo(() => {
    const match = location.pathname.match(/^\/ttq\/([^/]+)/);
    return match?.[1] ? decodeURIComponent(match[1]) : '';
  }, [location.pathname]);

  useEffect(() => {
    if (!moreOpen) return undefined;
    const onPointerDown = (event: MouseEvent) => {
      if (!moreRef.current?.contains(event.target as Node)) {
        setMoreOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [moreOpen]);

  useEffect(() => {
    const el = cardRef.current;
    if (!el || !onMeasureHeight) return undefined;
    const report = () => {
      const height = Math.ceil(el.offsetHeight);
      const width = Math.ceil(el.offsetWidth);
      if (height > 0 && width > 0) {
        onMeasureHeight(card.particleId, { width, height });
      }
    };
    const observer = new ResizeObserver(() => {
      report();
    });
    observer.observe(el);
    report();
    return () => observer.disconnect();
  }, [
    answersOpen,
    card.particleId,
    onMeasureHeight,
    provenanceOpen,
    materialOpen,
  ]);

  const needsModelMeta = !formatTtqModelAuditDisplay(provenance?.modelAudit);
  const { particle, loading: answersLoading, replaceParticle } = useTtqWorkbenchParticle(card.particleId, {
    // 衍生粒尽早加载 metadata，补全 L1/L2、锚点/解法路径与模型徽标
    enabled: answersOpen || isDerived || needsModelMeta,
  });

  const effectiveProvenance = useMemo(
    () => mergeProvenanceFromParticleMetadata(provenance, particle?.metadata),
    [particle?.metadata, provenance],
  );

  const handleSaveAbbrExpr = async (abbrExpr: string) => {
    setAbbrExprSaving(true);
    setAbbrExprError(null);
    try {
      const current = particle
        || await knowledgeService.getKnowledgeParticle(card.particleId);
      const meta = current.metadata && typeof current.metadata === 'object'
        ? { ...(current.metadata as Record<string, unknown>) }
        : {};
      const workbench = meta.ttqWorkbench && typeof meta.ttqWorkbench === 'object'
        ? { ...(meta.ttqWorkbench as Record<string, unknown>) }
        : {};
      const expansion = workbench.expansionProvenance && typeof workbench.expansionProvenance === 'object'
        ? { ...(workbench.expansionProvenance as Record<string, unknown>) }
        : {};
      workbench.abbrExpr = abbrExpr;
      expansion.abbrExpr = abbrExpr;
      workbench.expansionProvenance = expansion;
      meta.ttqWorkbench = workbench;
      const updated = await knowledgeService.updateKnowledgeParticle(card.particleId, {
        metadata: meta,
      });
      replaceParticle(updated);
      setAbbrExprDialogOpen(false);
    } catch (saveError) {
      setAbbrExprError(saveError instanceof Error ? saveError.message : '保存失败');
    } finally {
      setAbbrExprSaving(false);
    }
  };

  const modelDisplay = useMemo(() => {
    const fromProvenance = formatTtqModelAuditDisplay(effectiveProvenance?.modelAudit);
    if (fromProvenance) return fromProvenance;
    return formatTtqModelAuditDisplay(resolveParticleModelAudit(particle?.metadata));
  }, [effectiveProvenance?.modelAudit, particle?.metadata]);

  const originPresentation = useMemo(
    () => (isDerived ? resolveDerivedOriginPresentation(effectiveProvenance) : null),
    [effectiveProvenance, isDerived],
  );

  const isCttqCard = useMemo(() => {
    const angle = String(effectiveProvenance?.ttqAngleId || effectiveProvenance?.pathId || '').trim();
    // 一解多题也有多成员，但不能当成 CTTQ 收敛卡（否则会露出「增加节点并更新聚合」等）
    if (angle.includes('pattern_unify')) return false;
    if (angle.includes('cttq_converge')) return true;
    return (effectiveProvenance?.memberParticleIds?.length ?? 0) >= 2;
  }, [effectiveProvenance]);

  const showMoreMenu = Boolean(isDerived || onDeleteDerived);
  const cttqBusy = Boolean(isCttqCard && (cttqAggregating || regeneratingDerivedId === card.particleId));
  const derivedBusy = Boolean(regeneratingDerivedId === card.particleId);

  const answerBlocks = useMemo(() => {
    if (!particle?.answers) {
      return { mainSegments: [] as ExplosionSegmentView[], subAnswers: [] as Array<{ order: number; segments: ExplosionSegmentView[] }> };
    }
    const display = resolveKnowledgeParticleQaDisplay({
      questionSource: null,
      answerSource: particle.answers,
    }).answerDisplay;

    const objects = [card.whole];
    let indexOffset = 0;
    const mainText = display.main.trim();
    const mainSegments = mainText
      ? buildSegments(card.particleId, objects, mainText, indexOffset, answerSegmentObjectId)
      : [];
    indexOffset += mainSegments.length;

    const subAnswers = display.items
      .map((item) => item.trim())
      .filter(Boolean)
      .map((item, order) => {
        const segments = buildSegments(
          card.particleId,
          objects,
          item,
          indexOffset,
          answerSegmentObjectId,
        );
        indexOffset += segments.length;
        return { order: order + 1, segments };
      });

    return { mainSegments, subAnswers };
  }, [card.particleId, card.whole, particle?.answers]);

  const previewQuestionDisplay = useMemo(() => {
    if (!preview) return null;
    return resolveKnowledgeParticleQaDisplay({
      questionSource: card.whole.particleEntry?.particleQuestions,
      answerSource: null,
      fallbackQuestionText: card.title,
    }).questionDisplay;
  }, [card.title, card.whole.particleEntry?.particleQuestions, preview]);

  const previewAnswerDisplay = useMemo(() => {
    if (!preview || !particle?.answers) return null;
    return resolveKnowledgeParticleQaDisplay({
      questionSource: null,
      answerSource: particle.answers,
    }).answerDisplay;
  }, [particle?.answers, preview]);

  const allSegments = [
    ...card.mainSegments,
    ...card.subQuestions.flatMap((item) => item.segments),
    ...answerBlocks.mainSegments,
    ...answerBlocks.subAnswers.flatMap((item) => item.segments),
  ];
  const segmentSelected = !preview && allSegments.some((s) => selectedSegmentObjectIds.includes(s.objectId));
  const imageSelected = !preview && selectedObjectId === card.imageObjectId;
  const wholeSelected = !preview && (
    selectedObjectId === card.whole.id
    || selectedObjectId === wholeObjectId(card.particleId)
  );

  const { data: sourceImage, loading: sourceImageLoading } = useParticleEmbeddedImage(
    card.particleId,
    true,
  );

  const hasCrop = Boolean(sourceImage?.crop);
  const hasFullSource = Boolean(sourceImage?.fullSourceUrl);
  const multimodalBias = useMemo(
    () => assessMultimodalBias({
      particleMetadata: (particle?.metadata ?? null) as Record<string, unknown> | null,
      hasEmbeddedCrop: hasCrop,
      multimodalAnalysis: sourceImage?.multimodalAnalysis ?? null,
    }),
    [hasCrop, particle?.metadata, sourceImage?.multimodalAnalysis],
  );
  const strongMultimodal = multimodalBias.strong;

  const left = preview ? undefined : card.x + (dragOffset?.x ?? 0);
  const top = preview ? undefined : card.y + (dragOffset?.y ?? 0);
  const imageWidth = Math.min(card.width - 48, preview ? 360 : 420);
  const imageHeight = Math.round(imageWidth * 0.52);

  const handleFramePointerDown = (event: React.PointerEvent) => {
    if (preview) {
      // 预览浮窗：交给外层 onCanvasPanStart / 鸟瞰容器处理平移
      if ((event.target as HTMLElement).closest('[data-ttq-toolbar], [data-ttq-scroll], button, a, input, textarea')) {
        return;
      }
      if (event.button === 0 || event.button === 1) {
        if (ttqCanvasGesture.isSuppressingUi()) return;
        event.stopPropagation();
        onCanvasPanStart?.(card.particleId, event.clientX, event.clientY, event.pointerId);
      }
      return;
    }
    if ((event.target as HTMLElement).closest('[data-ttq-segment], [data-ttq-image], [data-ttq-toolbar]')) return;
    if (ttqCanvasGesture.isSuppressingUi()) return;
    event.stopPropagation();
    onSelectWhole?.(card.whole.id);
    if (event.button === 0 || event.button === 1) {
      onCanvasPanStart?.(card.particleId, event.clientX, event.clientY, event.pointerId);
    }
  };

  return (
    <>
    <div
      ref={cardRef}
      data-ttq-particle={card.particleId}
      className={
        preview
          ? 'relative isolate select-none rounded-2xl border px-5 py-4 shadow-lg'
          : 'absolute isolate select-none rounded-2xl border px-5 py-4 backdrop-blur-[2px]'
      }
      style={{
        left,
        top,
        width: card.width,
        zIndex: preview
          ? undefined
          : wholeSelected || segmentSelected || imageSelected || materialOpen || answersOpen
            || provenanceOpen || moreOpen
            ? 40
            : 10,
        backgroundColor: isDerived
          ? 'rgba(240, 253, 250, 0.94)'
          : (preview ? 'rgba(255, 254, 247, 0.98)' : 'rgba(255, 254, 247, 0.48)'),
        borderColor: wholeSelected
          ? '#2563eb'
          : isDerived
            ? 'rgba(13, 148, 136, 0.45)'
            : 'rgba(221, 208, 188, 0.55)',
        boxShadow: wholeSelected
          ? '0 0 0 2px rgba(37,99,235,0.22), 0 6px 18px rgba(0,0,0,0.06)'
          : isDerived
            ? '0 1px 8px rgba(13, 148, 136, 0.12)'
            : '0 1px 6px rgba(0,0,0,0.04)',
      }}
      onClick={(event) => event.stopPropagation()}
      onPointerDown={handleFramePointerDown}
    >
      {displayNumber ? (
        <div
          className="pointer-events-none absolute inset-0 z-0 flex items-center justify-center overflow-hidden rounded-2xl"
          aria-hidden
        >
          <span
            className={
              'select-none font-black tabular-nums leading-none tracking-tight '
              + (isDerived ? 'text-teal-900/[0.09]' : 'text-stone-900/[0.08]')
            }
            style={{ fontSize: preview ? 72 : 140 }}
          >
            {displayNumber}
          </span>
        </div>
      ) : null}

      {materialOpen && hasFullSource ? (
        <div
          className="absolute right-full top-0 z-20 mr-2 w-[320px] rounded-xl border border-emerald-300 bg-white/95 p-2 shadow-lg"
          data-ttq-toolbar="true"
          data-birds-float=""
          onPointerDown={(event) => event.stopPropagation()}
        >
          <p className="mb-1 px-1 text-[10px] font-semibold text-emerald-700">原始素材参考</p>
          <TtqibEmbeddedImage
            fullSourceUrl={sourceImage?.fullSourceUrl}
            ossId={sourceImage?.ossId}
            height={220}
            width={300}
            zoomable
            zoomTitle="原始素材"
          />
        </div>
      ) : null}

      <div className="relative z-[1] mb-3 shrink-0">
        {/* 第一行：居中标题 + 右侧 Scan */}
        <div className="mb-2 flex items-start gap-2">
          <div className="min-w-0 flex-1">
            {isDerived ? (
              <div className="pointer-events-none min-w-0 text-center">
                <p className="text-[12px] font-bold leading-tight tracking-wide text-teal-800">
                  {originPresentation?.headline || '衍生知识颗粒'}
                </p>
                {originPresentation?.l1Label || originPresentation?.l2Label ? (
                  <p className="mt-0.5 text-[10px] leading-snug text-stone-500">
                    {originPresentation.l1Label ? (
                      <span>L1 · {originPresentation.l1Label}</span>
                    ) : null}
                    {originPresentation.l1Label && originPresentation.l2Label ? (
                      <span className="mx-1 text-stone-300">/</span>
                    ) : null}
                    {originPresentation.l2Label ? (
                      <span>L2 · {originPresentation.l2Label}</span>
                    ) : null}
                  </p>
                ) : null}
              </div>
            ) : (
              <div className="flex min-w-0 items-center gap-2">
                <span className="pointer-events-none shrink-0 text-[10px] font-semibold uppercase tracking-wide text-stone-500/80">
                  知识颗粒
                  {displayNumber ? (
                    <span className="ml-1.5 inline-flex items-center rounded bg-stone-900/10 px-1.5 py-0.5 text-[11px] font-bold normal-case tabular-nums tracking-normal text-stone-800">
                      {displayNumber}
                    </span>
                  ) : null}
                </span>
                {modelDisplay ? (
                  <span
                    className="inline-flex min-w-0 max-w-[11rem] items-center gap-1 truncate rounded-md border border-stone-200/80 bg-white/70 px-1.5 py-0.5 text-[10px] text-stone-600"
                    title={`生成模型：${modelDisplay.label}`}
                  >
                    {modelDisplay.iconUrl ? (
                      <img src={modelDisplay.iconUrl} alt="" className="h-3.5 w-3.5 shrink-0 rounded-sm" />
                    ) : null}
                    <span className="truncate font-medium">{modelDisplay.label}</span>
                  </span>
                ) : null}
              </div>
            )}
          </div>
          {onCenterInView ? (
            <button
              type="button"
              className="relative z-[60] inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-stone-300 bg-white/90 text-stone-700 shadow-sm hover:bg-stone-50 hover:text-ink"
              data-ttq-toolbar="true"
              aria-label="居中显示此节点"
              title="居中显示此节点"
              onPointerDown={(event) => {
                event.stopPropagation();
                event.preventDefault();
              }}
              onClick={(event) => {
                event.stopPropagation();
                onCenterInView(card.particleId);
              }}
            >
              <Scan size={18} />
            </button>
          ) : null}
        </div>

        {/* 第二行：左侧编号；右侧操作组（含 MoreVertical）整体靠右对齐 */}
        <div className="flex items-center gap-2" data-ttq-toolbar="true">
          {isDerived && displayNumber ? (
            <span className="pointer-events-none inline-flex shrink-0 items-center rounded bg-teal-900/10 px-1.5 py-0.5 text-[11px] font-bold tabular-nums text-teal-900">
              {displayNumber}
            </span>
          ) : null}

          <div className="relative z-[60] ml-auto flex shrink-0 flex-wrap items-center justify-end gap-1">
            {isDerived && modelDisplay ? (
              <span
                className="inline-flex max-w-[7.5rem] items-center gap-1 truncate rounded-md border border-stone-200/80 bg-white/70 px-1.5 py-0.5 text-[10px] text-stone-600"
                title={`生成模型：${modelDisplay.label}`}
              >
                {modelDisplay.iconUrl ? (
                  <img src={modelDisplay.iconUrl} alt="" className="h-3.5 w-3.5 shrink-0 rounded-sm" />
                ) : null}
                <span className="truncate font-medium">{modelDisplay.label}</span>
              </span>
            ) : null}
            {isDerived ? (
              <button
                type="button"
                className={
                  'inline-flex h-7 w-7 items-center justify-center rounded-md border '
                  + (provenanceOpen
                    ? 'border-sky-500 bg-sky-50 text-sky-700'
                    : 'border-stone-300 bg-white/80 text-stone-600 hover:bg-stone-50')
                }
                aria-label={provenanceOpen ? '隐藏来源逻辑' : '显示来源逻辑'}
                aria-pressed={provenanceOpen}
                title={provenanceOpen ? '隐藏来源逻辑' : '显示来源逻辑'}
                onPointerDown={(event) => {
                  event.stopPropagation();
                  setProvenanceOpen((open) => !open);
                  setMoreOpen(false);
                }}
                onClick={(event) => {
                  event.stopPropagation();
                }}
              >
                <Info size={14} />
              </button>
            ) : null}
            <div className="relative flex items-center gap-1">
              <button
                type="button"
                className={
                  'inline-flex h-7 w-7 items-center justify-center rounded-md border '
                  + (strongMultimodal
                    ? 'border-violet-300 bg-violet-50 text-violet-700'
                    : 'border-stone-300 bg-white/80 text-stone-500')
                  + (multimodalHintOpen ? ' ring-2 ring-violet-300/60' : '')
                }
                aria-label={multimodalBias.label}
                aria-expanded={multimodalHintOpen}
                title={multimodalBias.label}
                onPointerDown={(event) => {
                  event.stopPropagation();
                  event.preventDefault();
                  setMultimodalHintOpen((open) => !open);
                }}
                onClick={(event) => event.stopPropagation()}
              >
                {strongMultimodal ? <Shapes size={14} /> : <Type size={14} />}
              </button>
              {multimodalHintOpen ? (
                <span
                  className={
                    'absolute right-full top-1/2 z-30 mr-1 -translate-y-1/2 whitespace-nowrap rounded-md border px-2 py-1 text-[10px] font-semibold leading-none shadow-sm '
                    + (strongMultimodal
                      ? 'border-violet-200 bg-violet-50 text-violet-900'
                      : 'border-stone-200 bg-white text-stone-700')
                  }
                  title={multimodalBias.detail}
                  data-ttq-toolbar="true"
                  onPointerDown={(event) => event.stopPropagation()}
                >
                  {multimodalBias.label}
                </span>
              ) : null}
            </div>
            {hasFullSource ? (
              <button
                type="button"
                className={
                  'inline-flex h-7 w-7 items-center justify-center rounded-md border '
                  + (materialOpen
                    ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                    : 'border-stone-300 bg-white/80 text-stone-600 hover:bg-stone-50')
                }
                aria-label={materialOpen ? '隐藏原始素材' : '显示原始素材'}
                aria-pressed={materialOpen}
                title={materialOpen ? '隐藏原始素材' : '显示原始素材'}
                onPointerDown={(event) => {
                  event.stopPropagation();
                  event.preventDefault();
                  setMaterialOpen((open) => !open);
                }}
              >
                {materialOpen ? <ImageIcon size={14} /> : <ImageOff size={14} />}
              </button>
            ) : null}
            <Link
              to={knowledgeDetailHref}
              className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-stone-300 bg-white/80 text-stone-600 hover:bg-stone-50"
              aria-label="查看知识颗粒详情"
              title="查看知识颗粒详情"
              onPointerDown={(event) => {
                event.stopPropagation();
              }}
              onClick={(event) => {
                event.stopPropagation();
                if (ttqaIdFromPath) {
                  markTtqReturnFromKnowledge(ttqaIdFromPath, card.particleId);
                }
              }}
            >
              <Hexagon size={14} />
            </Link>
            {showMoreMenu ? (
              <div ref={moreRef} className="relative shrink-0">
                <button
                  type="button"
                  className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-stone-300 bg-white/80 text-stone-600 hover:bg-stone-50"
                  aria-label="更多操作"
                  aria-expanded={moreOpen}
                  title="更多操作"
                  onPointerDown={(event) => {
                    event.stopPropagation();
                    setMoreOpen((open) => !open);
                  }}
                  onClick={(event) => {
                    event.stopPropagation();
                  }}
                >
                  <MoreVertical size={14} />
                </button>
                {moreOpen ? (
                  <div className="absolute right-0 top-[calc(100%+4px)] z-30 min-w-[11.5rem] overflow-hidden rounded-lg border border-border bg-surface py-1 shadow-lg">
                    {isCttqCard ? (
                      <>
                        {onAddCttqMember ? (
                          <button
                            type="button"
                            disabled={cttqBusy}
                            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-ink hover:bg-black/[0.04] disabled:opacity-50"
                            onPointerDown={(event) => {
                              event.stopPropagation();
                              event.preventDefault();
                              if (cttqBusy) return;
                              setMoreOpen(false);
                              onAddCttqMember(card.particleId);
                            }}
                          >
                            <Combine size={14} className={cttqBusy ? 'animate-pulse' : ''} />
                            {cttqBusy ? '聚合中…' : '增加节点并更新聚合'}
                          </button>
                        ) : null}
                        {onRegenerateDerived ? (
                          <button
                            type="button"
                            disabled={cttqBusy}
                            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-ink hover:bg-black/[0.04] disabled:opacity-50"
                            onPointerDown={(event) => {
                              event.stopPropagation();
                              event.preventDefault();
                              if (cttqBusy) return;
                              setMoreOpen(false);
                              onRegenerateDerived(card.particleId);
                            }}
                          >
                            <RefreshCw size={14} className={cttqBusy ? 'animate-spin' : ''} />
                            {cttqBusy ? '聚合中…' : '重新聚合'}
                          </button>
                        ) : null}
                        <button
                          type="button"
                          disabled={cttqBusy || abbrExprSaving}
                          className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-ink hover:bg-black/[0.04] disabled:opacity-50"
                          onPointerDown={(event) => {
                            event.stopPropagation();
                            event.preventDefault();
                            if (cttqBusy || abbrExprSaving) return;
                            setMoreOpen(false);
                            setAbbrExprError(null);
                            setAbbrExprDialogOpen(true);
                          }}
                        >
                          <Sigma size={14} />
                          更新缩略表达式
                        </button>
                        {onDeleteDerived ? (
                          <div className="my-1 border-t border-border" />
                        ) : null}
                        {onDeleteDerived ? (
                          <button
                            type="button"
                            disabled={cttqBusy}
                            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-danger hover:bg-danger/5 disabled:opacity-50"
                            onPointerDown={(event) => {
                              event.stopPropagation();
                              event.preventDefault();
                              if (cttqBusy) return;
                              setMoreOpen(false);
                              onDeleteDerived(card.particleId);
                            }}
                          >
                            <Trash2 size={14} />
                            删除
                          </button>
                        ) : null}
                      </>
                    ) : isDerived ? (
                      <>
                        {onRegenerateDerived ? (
                          <button
                            type="button"
                            disabled={derivedBusy}
                            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-ink hover:bg-black/[0.04] disabled:opacity-50"
                            onPointerDown={(event) => {
                              event.stopPropagation();
                              event.preventDefault();
                              if (derivedBusy) return;
                              setMoreOpen(false);
                              onRegenerateDerived(card.particleId);
                            }}
                          >
                            <RefreshCw size={14} className={derivedBusy ? 'animate-spin' : ''} />
                            {derivedBusy ? '生成中…' : '重新生成'}
                          </button>
                        ) : null}
                        <button
                          type="button"
                          disabled={derivedBusy || abbrExprSaving}
                          className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-ink hover:bg-black/[0.04] disabled:opacity-50"
                          onPointerDown={(event) => {
                            event.stopPropagation();
                            event.preventDefault();
                            if (derivedBusy || abbrExprSaving) return;
                            setMoreOpen(false);
                            setAbbrExprError(null);
                            setAbbrExprDialogOpen(true);
                          }}
                        >
                          <Sigma size={14} />
                          更新缩略表达式
                        </button>
                        {onDeleteDerived ? (
                          <div className="my-1 border-t border-border" />
                        ) : null}
                        {onDeleteDerived ? (
                          <button
                            type="button"
                            disabled={derivedBusy}
                            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-danger hover:bg-danger/5 disabled:opacity-50"
                            onPointerDown={(event) => {
                              event.stopPropagation();
                              event.preventDefault();
                              if (derivedBusy) return;
                              setMoreOpen(false);
                              onDeleteDerived(card.particleId);
                            }}
                          >
                            <Trash2 size={14} />
                            删除
                          </button>
                        ) : null}
                      </>
                    ) : onDeleteDerived ? (
                      <button
                        type="button"
                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-danger hover:bg-danger/5"
                        onPointerDown={(event) => {
                          event.stopPropagation();
                          event.preventDefault();
                          setMoreOpen(false);
                          onDeleteDerived(card.particleId);
                        }}
                      >
                        <Trash2 size={14} />
                        移出品题
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>

        {isDerived && effectiveProvenance?.abbrExpr ? (
          <div
            className="pointer-events-none mt-1.5 min-w-0 max-w-full overflow-x-auto"
            title={effectiveProvenance.abbrExpr}
          >
            <span className="inline-flex min-w-0 items-center rounded-md border border-teal-200/70 bg-white/75 px-1.5 py-0.5 text-[11px] leading-none text-teal-900/90">
              <DocumentMathTextBlock text={effectiveProvenance.abbrExpr} inline />
            </span>
          </div>
        ) : null}
      </div>

      {isDerived && provenanceOpen ? (
        <div
          className="mb-3 rounded-lg border border-sky-200 bg-sky-50/80 px-3 py-2 text-[12px] leading-relaxed text-ink"
          data-ttq-toolbar="true"
          data-ttq-scroll="true"
          onPointerDown={(event) => event.stopPropagation()}
          onWheel={(event) => event.stopPropagation()}
        >
          {modelDisplay ? (
            <p className="flex items-center gap-1.5 text-muted">
              生成模型：
              {modelDisplay.iconUrl ? (
                <img src={modelDisplay.iconUrl} alt="" className="h-3.5 w-3.5 rounded-sm" />
              ) : null}
              <span className="font-medium text-ink/80">{modelDisplay.label}</span>
            </p>
          ) : null}
          {originPresentation?.detailKind === 'anchor' ? (
            effectiveProvenance?.anchorText ? (
              <p className={`${modelDisplay ? 'mt-1' : ''} text-muted`}>
                题面锚点：
                <span className="text-ink/80">「{effectiveProvenance.anchorText}」</span>
              </p>
            ) : (
              <p className={`${modelDisplay ? 'mt-1' : ''} text-muted`}>
                题面锚点：整题（无爆炸选段）
              </p>
            )
          ) : null}
          {originPresentation?.detailKind === 'approach' ? (
            effectiveProvenance?.customPrompt || (effectiveProvenance?.strategyLabel && effectiveProvenance.strategyLabel !== '一题多解') ? (
              <p className={`${modelDisplay ? 'mt-1' : ''} text-muted`}>
                解法路径：
                <span className="text-ink/80">
                  {effectiveProvenance?.customPrompt
                    || effectiveProvenance?.strategyLabel
                    || '一题多解'}
                </span>
              </p>
            ) : (
              <p className={`${modelDisplay ? 'mt-1' : ''} text-muted`}>
                解法路径：一题多解（早期跑次未写入路径说明）
              </p>
            )
          ) : null}
          {effectiveProvenance?.directionPreview ? (
            <p className="mt-1 text-muted">方向预览：{effectiveProvenance.directionPreview}</p>
          ) : null}
          {effectiveProvenance?.generationPrompt ? (
            <div className="mt-2">
              <p className="text-[11px] font-medium text-sky-800">生成提示词（落库回放）</p>
              <pre
                data-ttq-scroll="true"
                className="mt-1 max-h-40 overflow-y-auto whitespace-pre-wrap break-words rounded-md border border-sky-100 bg-white/70 px-2 py-1.5 font-sans text-[11px] text-ink/85"
                onWheel={(event) => event.stopPropagation()}
              >
                {effectiveProvenance.generationPrompt}
              </pre>
            </div>
          ) : (
            <p className="mt-1 text-muted">生成提示词：未落库（早期跑次；请重新生成以写入）</p>
          )}
          {effectiveProvenance?.customPrompt
            && originPresentation?.detailKind === 'anchor' ? (
            <p className="mt-1 text-muted">衍生说明：{effectiveProvenance.customPrompt}</p>
          ) : null}
          {!effectiveProvenance ? (
            <p className="mt-1 text-muted">暂无品题路径记录。</p>
          ) : null}
          <p
            className="mt-1.5 cursor-pointer select-text font-mono text-[10px] tracking-wide text-stone-400/90 hover:text-stone-600"
            title="点击复制颗粒 ID"
            onPointerDown={(event) => event.stopPropagation()}
            onClick={(event) => {
              event.stopPropagation();
              event.preventDefault();
              const id = String(card.particleId || '').trim();
              if (!id) return;
              void navigator.clipboard?.writeText(id).catch(() => undefined);
            }}
          >
            {card.particleId}
          </p>
        </div>
      ) : null}

      {hasCrop ? (
        <button
          type="button"
          data-ttq-image="true"
          data-object-id={card.imageObjectId}
          aria-pressed={imageSelected}
          className={`ttq-explosion-image mb-3 block shrink-0 self-start overflow-hidden rounded-lg bg-transparent p-0 text-left ${
            imageSelected ? 'is-selected' : ''
          }`}
          onPointerDown={(event) => {
            event.stopPropagation();
            event.preventDefault();
            if (!preview) onSelectImage?.(card.imageObjectId);
          }}
        >
          {sourceImageLoading ? (
            <p className="py-4 text-xs text-muted">加载嵌入图…</p>
          ) : (
            <TtqibEmbeddedImage
              record={sourceImage?.crop}
              ossId={sourceImage?.ossId}
              height={imageHeight}
              width={imageWidth}
              zoomable
              zoomTitle="嵌入图"
            />
          )}
        </button>
      ) : null}

      {preview && previewQuestionDisplay ? (
        <PreviewQaSections
          mainText={previewQuestionDisplay.main}
          items={previewQuestionDisplay.items}
          emptyLabel="（无题干）"
        />
      ) : (
        <QaBlockList
          mainSegments={card.mainSegments}
          subs={card.subQuestions}
          selectedSegmentObjectIds={selectedSegmentObjectIds}
          tastedAnchorTexts={tastedAnchorTexts}
          chatAnchors={chatAnchors}
          particleId={card.particleId}
          onToggleSegment={onToggleSegment}
          onOpenChatThread={onOpenChatThread}
          onOpenPinTi={onOpenPinTi}
          onOpenLadderPractice={onOpenLadderPractice}
          showLadderPractice={showLadderPractice}
          emptyLabel="（无题干）"
          showPinTiToolbar={!preview}
          readOnly={preview}
        />
      )}

      {!isDerived && linkedRefs.length > 0 ? (
        <div
          className="mt-3 rounded-lg border border-stone-200/80 bg-white/50 px-3 py-2"
          data-ttq-toolbar="true"
          onPointerDown={(event) => event.stopPropagation()}
        >
          <p className="mb-1.5 text-[10px] font-medium uppercase tracking-wide text-stone-500">
            关联题 · 引用
          </p>
          <ul className="space-y-1">
            {linkedRefs.map((ref) => (
              <li key={ref.particleId}>
                <button
                  type="button"
                  className="w-full truncate text-left text-[12px] text-stone-600 hover:text-primary"
                  title={ref.title}
                  onClick={() => onFocusLinked?.(ref.particleId)}
                >
                  {ref.title}
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <button
        type="button"
        className="mt-4 flex h-7 w-full items-center justify-center text-[12px] tracking-wide text-stone-500/90 hover:text-stone-700"
        data-ttq-toolbar="true"
        aria-label={answersOpen ? '点击收起答案' : '点击展开答案'}
        aria-pressed={answersOpen}
        title={answersOpen ? '点击收起答案' : '点击展开答案'}
        onPointerDown={(event) => {
          event.stopPropagation();
          event.preventDefault();
          setAnswersOpen((open) => !open);
        }}
      >
        {answersOpen ? '点击收起答案' : '———参考答案———'}
      </button>

      {answersOpen ? (
        <div className="mt-3">
          {answersLoading ? (
            <p className="text-sm text-muted">加载答案…</p>
          ) : preview && previewAnswerDisplay ? (
            <PreviewQaSections
              mainText={previewAnswerDisplay.main}
              items={previewAnswerDisplay.items}
              emptyLabel="（无答案）"
            />
          ) : (
            <QaBlockList
              mainSegments={answerBlocks.mainSegments}
              subs={answerBlocks.subAnswers}
              selectedSegmentObjectIds={selectedSegmentObjectIds}
              tastedAnchorTexts={tastedAnchorTexts}
              chatAnchors={chatAnchors}
              particleId={card.particleId}
              onToggleSegment={onToggleSegment}
              onOpenChatThread={onOpenChatThread}
              onOpenPinTi={onOpenPinTi}
              emptyLabel="（无答案）"
              readOnly={preview}
            />
          )}
        </div>
      ) : null}
    </div>
    <TtqUpdateAbbrExprDialog
      open={abbrExprDialogOpen}
      particleId={card.particleId}
      initialAbbrExpr={effectiveProvenance?.abbrExpr}
      submitting={abbrExprSaving}
      error={abbrExprError}
      onCancel={() => {
        if (!abbrExprSaving) {
          setAbbrExprDialogOpen(false);
          setAbbrExprError(null);
        }
      }}
      onConfirm={(abbrExpr) => {
        void handleSaveAbbrExpr(abbrExpr);
      }}
    />
    </>
  );
}
